import { ResourceType } from "@istari-frontend/resource/model";
import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";

import { applyChatSaveFollowUps } from "~/ai-chat/feature/apply-chat-save-follow-ups";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { buildReferencedFileName } from "~/ai-chat/model/chat-message";
import type { ChatSaveChoices, ChatSaveDestination, ChatSaveOutcome } from "~/ai-chat/model/chat-save";
import { extensionOf, inferMimeType, type SaveResourceAction } from "~/ai-chat/model/save-resource-action";
import { usePatchModelControlTags } from "~/api/useControlTags";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";
import { useCommitFileToBranch } from "~/branching/feature/use-commit-file-to-branch";
import {
  assertUploadSessionCurrent,
  awaitUploadSessionReady,
  captureUploadSession,
} from "~/resource/data-source/upload-session-callbacks";
import { useCreateResource } from "~/resource/data-source/use-create-resource";
import { useCreateResourceFromRevision } from "~/resource/data-source/use-create-resource-from-revision";

export interface SavedChatResource extends ChatSaveOutcome {
  resourceId: string;
  fileRevisionId: string;
}

interface SaveResourceActionApi {
  // `action` carries the card's edits, so its file name may be renamed; `proposalFileName` is the
  // assistant's original one, which keys the draft this save turns into.
  save: (
    action: SaveResourceAction,
    choices?: ChatSaveChoices,
    proposalFileName?: string,
  ) => Promise<SavedChatResource>;
  isPending: boolean;
}

export function useSaveResourceAction(destination?: ChatSaveDestination): SaveResourceActionApi {
  const { addReferencedFiles, recordSavedArtifact, getConversationEpoch } = useAiChat();
  const createResource = useCreateResource();
  const createResourceFromRevision = useCreateResourceFromRevision();
  const assignInfosecLevel = useAssignInfosecLevel();
  // The card reports a tag miss in its own warning; the hook's toast would duplicate it and,
  // from a popped-out chat, land in the main window.
  const patchControlTags = usePatchModelControlTags({ onError: () => undefined });
  const commitFileToBranch = useCommitFileToBranch(destination?.systemId);

  const save = useCallback(
    async (
      action: SaveResourceAction,
      choices: ChatSaveChoices = {},
      proposalFileName = action.fileName,
    ): Promise<SavedChatResource> => {
      const session = captureUploadSession();
      assertUploadSessionCurrent(session);
      const conversationEpoch = getConversationEpoch();
      await awaitUploadSessionReady(session);
      // Model, not File: the Files explorer lists only Model/Artifact resources
      // (FILES_EXPLORER_RESOURCE_TYPES), and manual uploads default to Model —
      // a "file"-typed resource would be invisible in the library.
      const resource = action.staged
        ? // A tool already uploaded the bytes; only the resource row is created here, so a
          // Word document gets exactly the save step a text file does.
          await createResourceFromRevision.mutateAsync({
            resourceType: ResourceType.Model,
            name: action.fileName,
            extension: extensionOf(action.fileName) || null,
            size: action.staged.size,
            mime: action.staged.mime ?? inferMimeType(action.fileName),
            displayName: action.displayName,
            description: action.description ?? null,
            contentToken: action.staged.contentToken,
            propertiesToken: action.staged.propertiesToken,
          })
        : await createResource.mutateAsync({
            intent: "generated",
            file: new File([action.content], action.fileName, { type: inferMimeType(action.fileName) }),
            displayName: action.displayName,
            description: action.description,
            resourceType: V3Models.ResourceTypeDto.Model,
          });

      await awaitUploadSessionReady(session);
      const outcome = await applyChatSaveFollowUps(
        { resourceId: resource.resourceId, fileRevisionId: resource.fileRevisionId, displayName: action.displayName },
        choices,
        destination,
        {
          assignInfosecLevel: async (id, infosecLevelId) => {
            await awaitUploadSessionReady(session);
            return assignInfosecLevel.mutateAsync({ type: "model", id, infosecLevelId });
          },
          assignControlTags: async (modelId, ids) => {
            await awaitUploadSessionReady(session);
            return patchControlTags.mutateAsync({ modelId, controlTagIdsActionTypes: [{ ids, action: "set" }] });
          },
          addToBranch: async (fileRevisionId, displayName, target) => {
            await awaitUploadSessionReady(session);
            return commitFileToBranch.mutateAsync({
              branchId: target.branch.id,
              expectedHeadSnapshotId: target.branch.headCommitId,
              fileRevisionId,
              commitMessage: `Add ${displayName}`,
            });
          },
        },
      );

      await awaitUploadSessionReady(session);
      // The save may outlive the chat it started in; a resource saved from the previous
      // conversation must not attach to the composer or the Artifacts panel of the one now on screen.
      if (getConversationEpoch() === conversationEpoch) {
        const name = buildReferencedFileName(action.displayName, extensionOf(action.fileName));
        addReferencedFiles([
          { resourceId: resource.resourceId, name, fileRevisionId: resource.fileRevisionId, origin: "chat-save" },
        ]);
        recordSavedArtifact({
          resourceId: resource.resourceId,
          name,
          fileName: proposalFileName,
          content: action.content,
        });
      }
      return { resourceId: resource.resourceId, fileRevisionId: resource.fileRevisionId, ...outcome };
    },
    [
      createResource,
      createResourceFromRevision,
      assignInfosecLevel,
      patchControlTags,
      commitFileToBranch,
      destination,
      addReferencedFiles,
      recordSavedArtifact,
      getConversationEpoch,
    ],
  );

  return {
    save,
    isPending:
      createResource.isPending ||
      createResourceFromRevision.isPending ||
      assignInfosecLevel.isPending ||
      patchControlTags.isPending ||
      commitFileToBranch.isPending,
  };
}
